import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Search,
  BellRing,
  BarChart,
  Calendar,
  Users,
  Target,
  TrendingUp,
  Play,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import AdminLayout from "@/components/layout/AdminLayout";

import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Campaign {
  id: number;
  title: string; // Changed from 'name' to 'title' to match database schema
  description: string;
  type: string;
  status: string;
  target_count: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  scheduled_at: string; // Changed from 'scheduled_for' to 'scheduled_at'
  created_at: string;
  user_groups?: {
    name: string;
  };
}

const NotificationCampaignsList = () => {
  const { toast } = useToast();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sendingNowId, setSendingNowId] = useState<number | null>(null);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("group_campaigns")
        .select(
          `
          *,
          user_groups (
            name
          )
        `
        )
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setError("Failed to load campaigns. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendNow = async (campaignId: number) => {
    try {
      setSendingNowId(campaignId);

      // Update campaign status to "sending" and set scheduled_at to now
      const { error: updateError } = await supabase
        .from("group_campaigns")
        .update({
          status: "sending",
          scheduled_at: new Date().toISOString(),
        })
        .eq("id", campaignId);

      if (updateError) throw updateError;

      // Trigger the process-scheduled-campaigns edge function
      const { error: fnError } = await supabase.functions.invoke(
        "process-scheduled-campaigns",
        { body: { campaign_id: campaignId } }
      );

      if (fnError) throw fnError;

      toast({
        title: "Campaign Sent",
        description: "The campaign is now being processed and sent.",
      });

      // Refresh campaign list
      await fetchCampaigns();
    } catch (err) {
      console.error("Error sending campaign:", err);
      toast({
        title: "Error",
        description: "Failed to send campaign. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSendingNowId(null);
    }
  };

  const filteredCampaigns = campaigns.filter((campaign) => {
    // Safe string operations with null checks
    const name = (campaign.title || "").toLowerCase();
    const description = (campaign.description || "").toLowerCase();
    const searchLower = searchTerm.toLowerCase();

    const matchesSearch =
      name.includes(searchLower) || description.includes(searchLower);
    const matchesStatus =
      statusFilter === "all" || campaign.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadgeColor = (status: string) => {
    const colors = {
      draft: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
      scheduled:
        "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      sending:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
      sent: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
      cancelled:
        "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
    };
    return (
      colors[status as keyof typeof colors] ||
      "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
    );
  };

  const getTypeBadgeColor = (type: string) => {
    const colors = {
      notification:
        "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      promotion:
        "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
      offer:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      announcement:
        "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
    };
    return (
      colors[type as keyof typeof colors] ||
      "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
    );
  };

  const calculateEngagementRate = (campaign: Campaign) => {
    if (campaign.delivered_count === 0) return 0;
    return Math.round((campaign.opened_count / campaign.delivered_count) * 100);
  };

  const calculateDeliveryRate = (campaign: Campaign) => {
    if (campaign.sent_count === 0) return 0;
    return Math.round((campaign.delivered_count / campaign.sent_count) * 100);
  };

  const calculateClickRate = (campaign: Campaign) => {
    if (campaign.opened_count === 0) return 0;
    return Math.round((campaign.clicked_count / campaign.opened_count) * 100);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not scheduled";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "draft":
        return "📝";
      case "scheduled":
        return "⏰";
      case "sending":
        return "📤";
      case "sent":
        return "✅";
      case "failed":
        return "❌";
      case "cancelled":
        return "🚫";
      default:
        return "📋";
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <AdminLayout title="Campaign Management">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Campaign Management
            </h1>
            <p className="text-muted-foreground">
              Create and manage targeted notification campaigns
            </p>
          </div>
          <Button asChild>
            <Link to="/admin/notifications/campaigns/new">
              <Plus className="h-4 w-4 mr-2" />
              New Campaign
            </Link>
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <BellRing className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Total Campaigns</p>
                  <p className="text-2xl font-bold">{campaigns.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Target className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Active</p>
                  <p className="text-2xl font-bold">
                    {
                      campaigns.filter(
                        (c) =>
                          c.status === "scheduled" || c.status === "sending"
                      ).length
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Total Sent</p>
                  <p className="text-2xl font-bold">
                    {campaigns
                      .reduce((sum, c) => sum + (c.sent_count || 0), 0)
                      .toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Avg. Open Rate</p>
                  <p className="text-2xl font-bold">
                    {campaigns.length > 0
                      ? Math.round(
                          campaigns.reduce(
                            (sum, c) => sum + calculateEngagementRate(c),
                            0
                          ) / campaigns.length
                        )
                      : 0}
                    %
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BellRing className="h-5 w-5" />
              Campaigns ({filteredCampaigns.length} of {campaigns.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search campaigns by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="sending">Sending</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Target Group</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Performance</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCampaigns.map((campaign) => (
                  <TableRow key={campaign.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          <span>{getStatusIcon(campaign.status)}</span>
                          {campaign.title || "Untitled Campaign"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {campaign.description || "No description"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Created{" "}
                          {new Date(campaign.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getTypeBadgeColor(campaign.type)}>
                        {campaign.type || "notification"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {campaign.user_groups?.name || "No Group"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {campaign.target_count?.toLocaleString() || 0} users
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeColor(campaign.status)}>
                        {campaign.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2 min-w-[200px]">
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span>Delivery:</span>
                            <span>{calculateDeliveryRate(campaign)}%</span>
                          </div>
                          <Progress
                            value={calculateDeliveryRate(campaign)}
                            className="h-1.5"
                          />
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span>Opens:</span>
                            <span>{calculateEngagementRate(campaign)}%</span>
                          </div>
                          <Progress
                            value={calculateEngagementRate(campaign)}
                            className="h-1.5"
                          />
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span>Clicks:</span>
                            <span>{calculateClickRate(campaign)}%</span>
                          </div>
                          <Progress
                            value={calculateClickRate(campaign)}
                            className="h-1.5"
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        {formatDate(campaign.scheduled_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {(campaign.status === "draft" || campaign.status === "scheduled") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendNow(campaign.id)}
                            disabled={sendingNowId === campaign.id}
                          >
                            {sendingNowId === campaign.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Play className="h-4 w-4 mr-1" />
                                Send Now
                              </>
                            )}
                          </Button>
                        )}
                        <Button variant="outline" size="sm" asChild>
                          <Link
                            to={`/admin/notifications/campaigns/${campaign.id}/analytics`}
                          >
                            <BarChart className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <Link
                            to={`/admin/notifications/campaigns/${campaign.id}/edit`}
                          >
                            Edit
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredCampaigns.length === 0 && (
              <div className="text-center py-12">
                <BellRing className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {searchTerm || statusFilter !== "all"
                    ? "No campaigns match your filters"
                    : "No campaigns found"}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || statusFilter !== "all"
                    ? "Try adjusting your search criteria or status filter"
                    : "Get started by creating your first notification campaign"}
                </p>
                {!searchTerm && statusFilter === "all" && (
                  <Button asChild>
                    <Link to="/admin/notifications/campaigns/new">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First Campaign
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default NotificationCampaignsList;
